import type { ConnectionItemProps } from "@wayflyer/flyui";
import { IconPage24Line } from "@wayflyer/flyui-icons-next/24/line";
import type { FundingApplicationSchemas } from "api-types";
import type { MessageDescriptor } from "react-intl";
import { useIntl } from "react-intl";
import { useSpaNavigate } from "shared-ui";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { CONNECT_APP_ROUTE_PATHS } from "../../../components/constants";
import type { FormattedFundingApplicationScreen } from "../../../queries/funding-application/queries";
import { UploadDocumentType, type BusinessModelData } from "../../../types";
import {
  isAccountingUploadRequired,
  isCashflowRequired,
  isDebtAgreementRequired,
  isDocumentOptional,
  isPoolBancarioRequired,
  isSusaRequired,
} from "../../../utils/funding-journey";

const DOC_TYPE_TO_NAME: Record<UploadDocumentType, MessageDescriptor> = {
  [UploadDocumentType.SUSA]: COMMON_MESSAGES.documentUploadSusa,
  [UploadDocumentType.POOL_BANCARIO]: COMMON_MESSAGES.documentUploadPoolBancario,
  [UploadDocumentType.CASH_FLOW_FORECAST]: COMMON_MESSAGES.documentUploadRevenueForecast,
  [UploadDocumentType.COMPANY_DEBT_AGREEMENT]: COMMON_MESSAGES.documentUploadDebtAgreement,
  [UploadDocumentType.MANAGEMENT_ACCOUNTS]: COMMON_MESSAGES.documentUploadManagementAccounts,
  [UploadDocumentType.PROFIT_AND_LOSS]: COMMON_MESSAGES.documentUploadProfitAndLoss,
  [UploadDocumentType.BALANCE_SHEET]: COMMON_MESSAGES.documentUploadBalanceSheet,
};

export const useAdditionalDocumentsAsConnectionItems = (
  fundingApplication: FormattedFundingApplicationScreen,
  countryOfIncorporation: string,
  onDocumentRemove: (type: UploadDocumentType, required: boolean) => void,
  businessModel: BusinessModelData,
  documents?: FundingApplicationSchemas.ListDocumentsResponse[]
): ConnectionItemProps[] => {
  const { navigate } = useSpaNavigate("FundingApp");
  const intl = useIntl();

  const requiredDocuments = [
    ...(isSusaRequired(countryOfIncorporation, fundingApplication) ? [UploadDocumentType.SUSA] : []),
    ...(isPoolBancarioRequired(countryOfIncorporation) ? [UploadDocumentType.POOL_BANCARIO] : []),

    ...(isAccountingUploadRequired(businessModel, fundingApplication, countryOfIncorporation) &&
    documents?.some((doc) => doc.documentType === UploadDocumentType.MANAGEMENT_ACCOUNTS)
      ? [UploadDocumentType.MANAGEMENT_ACCOUNTS]
      : []),
    ...(isDebtAgreementRequired(fundingApplication, businessModel)
      ? [UploadDocumentType.COMPANY_DEBT_AGREEMENT] // same order as the steps in the linear flow
      : []),
    ...(isCashflowRequired(fundingApplication, businessModel) ? [UploadDocumentType.CASH_FLOW_FORECAST] : []),
  ];

  // Document type marked by customer as "we don't have this document"
  const isDocumentMarkedAsSkipped = {
    [UploadDocumentType.SUSA]: false,
    [UploadDocumentType.POOL_BANCARIO]: fundingApplication.isPoolBancarioSkipped,
    [UploadDocumentType.CASH_FLOW_FORECAST]: fundingApplication.isCashflowForecastSkipped,
    [UploadDocumentType.COMPANY_DEBT_AGREEMENT]: fundingApplication.isCompanyDebtAgreementSkipped,
    [UploadDocumentType.MANAGEMENT_ACCOUNTS]: fundingApplication.accountingOptedOut,
    [UploadDocumentType.PROFIT_AND_LOSS]: fundingApplication.accountingOptedOut,
    [UploadDocumentType.BALANCE_SHEET]: fundingApplication.accountingOptedOut,
  };

  const items: ConnectionItemProps[] = [];

  requiredDocuments.forEach((documentType) => {
    const matchingDocuments = documents ? documents.find((upload) => upload.documentType === documentType) : undefined;

    if (isDocumentMarkedAsSkipped[documentType]) {
      items.push({
        testId: `document-skipped-${documentType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[documentType]),
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage({ id: "funding-application:completed", defaultMessage: "Completed" }),
      });
    } else if (matchingDocuments) {
      items.push({
        testId: `document-uploaded-${documentType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[documentType]),
        logo: <IconPage24Line />,
        badgeStatus: "positive",
        badgeText: intl.formatMessage(COMMON_MESSAGES.uploaded),
        subtitle: (
          <ul>
            {matchingDocuments.data.map((doc) => (
              <li key={doc.id}>{doc.name}</li>
            ))}
          </ul>
        ),
      });
    } else {
      items.push({
        testId: `document-todo-${documentType}`,
        label: intl.formatMessage(DOC_TYPE_TO_NAME[documentType]),
        logo: <IconPage24Line />,
        actions: [
          {
            critical: true,
            content: intl.formatMessage({
              id: "funding-application: remove platform label",
              defaultMessage: "Remove",
            }),
            onAction: () => onDocumentRemove(documentType, isDocumentOptional(documentType, fundingApplication)),
          },
          {
            content: intl.formatMessage(COMMON_MESSAGES.upload),
            onAction: () => navigate(`${CONNECT_APP_ROUTE_PATHS.UPLOADS}?document=${documentType}`),
          },
        ],
      });
    }
  });
  return items;
};
